import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, ArrowUpRight, KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const tip = { contentStyle: { background: "#1A1D21", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, fontSize: 12 }, labelStyle: { color: "#A1A8B3" } };
const fmt = (v, suf = "") => (v == null ? "—" : `${v}${suf}`);

export default function Dashboard() {
  const [m, setM] = useState(null);
  const load = () => api.metrics().then(setM).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!m) return <p className="text-sm text-[#6E7582]">Loading…</p>;
  const t = m.totals;
  const today = m.series?.[m.series.length - 1] || {};
  const perSitting = t.sessions ? t.spend / t.sessions : null;
  const KPIS = [
    { label: "Sessions (14d)", value: t.sessions },
    { label: "Spend today", value: `$${(today.spend || 0).toFixed(2)}`, sub: `of $${m.daily_cap} cap` },
    { label: "Cost per sitting", value: perSitting == null ? "—" : `$${perSitting.toFixed(3)}`, sub: "14-day average" },
    { label: "Grounding rate", value: fmt(t.grounding_rate, "%") },
    { label: "Avg. session", value: t.avg_session_sec ? `${Math.floor(t.avg_session_sec / 60)}m ${t.avg_session_sec % 60}s` : "—" },
    { label: "Fallback to text", value: fmt(t.fallback_rate, "%") },
    { label: "Error rate", value: `${t.error_rate}%` },
    { label: "Spend this month", value: `$${m.month_spend.toFixed(2)}`, sub: `of $${m.monthly_cap} cap` },
  ];
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Overview</p>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">Pilot overview</h2>
        </div>
        <div className="flex gap-2">
          {!m.ai_configured && <span data-testid="ai-key-missing-badge" className="flex items-center gap-2 rounded-full border border-[#E9C46A]/30 bg-[#E9C46A]/10 px-3 py-1.5 text-xs text-[#E9C46A]"><KeyRound size={12} /> OpenAI key not configured</span>}
          <span className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${m.kill_switch ? "border-[#E63946]/30 bg-[#E63946]/10 text-[#E63946]" : "border-[#2EC4B6]/30 bg-[#2EC4B6]/10 text-[#2EC4B6]"}`} data-testid="system-status-badge">
            <span className={`h-2 w-2 rounded-full ${m.kill_switch ? "bg-[#E63946]" : "bg-[#2EC4B6]"}`} /> {m.kill_switch ? "Griot is paused" : "Griot is live"}
          </span>
        </div>
      </header>

      {m.credits_exhausted_at && (
        <div data-testid="credits-exhausted-banner" className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#E63946]/40 bg-[#E63946]/10 px-5 py-4">
          <AlertTriangle size={16} className="text-[#E63946]" />
          <p className="text-sm text-[#F3EFE6]">
            <span className="font-semibold">Griot has gone quiet: the AI credits have run out.</span>{" "}
            The provider last refused a request at {new Date(m.credits_exhausted_at).toLocaleString()}. Participants see the
            "resting" screen until the OpenAI account is topped up — nothing in this Lab's memory is lost.
          </p>
          <a href="https://platform.openai.com/settings/organization/billing" target="_blank" rel="noreferrer" className="rounded-full border border-[#E63946]/50 px-4 py-1.5 text-xs text-[#F4A261] hover:bg-[#E63946]/10">Top up</a>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {KPIS.map((k) => (
          <div key={k.label} className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs text-[#6E7582]">{k.label}</p>
            <p className="mt-2 font-display text-2xl font-semibold">{k.value}</p>
            {k.sub && <p className="mt-1 text-xs text-[#6E7582]">{k.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="min-w-0 rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4 sm:p-5 lg:col-span-3">
          <div className="flex items-baseline justify-between"><h3 className="font-display text-base font-medium">Sessions per day</h3><span className="text-xs text-[#6E7582]">last 14 days</span></div>
          <div className="mt-4 h-56">
            <ResponsiveContainer>
              <AreaChart data={m.series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E07A5F" stopOpacity={0.5} /><stop offset="100%" stopColor="#E07A5F" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="day" tick={{ fill: "#6E7582", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide allowDecimals={false} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="sessions" stroke="#F4A261" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="min-w-0 rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4 sm:p-5 lg:col-span-2">
          <div className="flex items-baseline justify-between"><h3 className="font-display text-base font-medium">Daily spend</h3><span className="font-mono text-xs text-[#6E7582]">USD · cap ${m.daily_cap}</span></div>
          <div className="mt-4 h-56">
            <ResponsiveContainer>
              <BarChart data={m.series}>
                <XAxis dataKey="day" tick={{ fill: "#6E7582", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip {...tip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="spend" fill="#E9C46A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-5" data-testid="spend-readout">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-base font-medium">Spend this month</h3>
          <span className="font-mono text-xs text-[#6E7582]">${m.month_spend.toFixed(2)} / ${m.monthly_cap} cap · edit in Studio</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/[0.06]">
          <div className="h-full rounded-full bg-gradient-to-r from-[#E07A5F] to-[#F4A261]" style={{ width: `${Math.min(100, (m.month_spend / (m.monthly_cap || 1)) * 100)}%` }} />
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-5">
        <h3 className="font-display text-base font-medium">Grounding rate</h3>
        <p className="text-xs text-[#6E7582]">Share of responses backed by at least one corpus chunk. Corpus: {t.corpus_docs} docs · {t.corpus_chunks} chunks.</p>
        <div className="mt-4 h-32">
          <ResponsiveContainer>
            <AreaChart data={m.series}>
              <XAxis dataKey="day" hide /><YAxis hide domain={[0, 100]} />
              <Tooltip {...tip} />
              <Area type="monotone" dataKey="grounding" connectNulls stroke="#2EC4B6" strokeWidth={2} fill="rgba(46,196,182,0.12)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <ResetStats onDone={load} />
      <p className="flex items-center gap-1 text-xs text-[#6E7582]"><ArrowUpRight size={12} /> Evaluation gates (grounding / safety / tone) arrive in Phase 3 and remain illustrative for now.</p>
    </div>
  );
}

/* Deploy copies the preview database into production, so a brand-new console opens full of the
   sessions the build and its tests made. This clears the record without touching the Lab. */
const ResetStats = ({ onDone }) => {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const wipe = async () => {
    setBusy(true);
    try {
      const r = await api.resetStats();
      const n = Object.values(r.wiped).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${n} records — the Lab, its corpus and its persona are untouched`);
      setArmed(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E07A5F]/20 bg-[#1A1D21] p-5" data-testid="reset-stats-card">
      <h3 className="font-display text-base font-medium">Start the record clean</h3>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[#6E7582]">
        Deleting every session, turn, usage row and client log for this Lab. Use it once after going live:
        deployment copies the preview database across, so the counters you inherit are the ones the build
        and its tests made. The corpus, persona, purpose, brief and openings are not touched.
      </p>
      {!armed ? (
        <button
          data-testid="reset-stats-btn"
          onClick={() => setArmed(true)}
          className="mt-4 rounded-full border border-[#E07A5F]/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#E07A5F] hover:bg-[#E07A5F]/10"
        >
          clear the statistics
        </button>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs text-[#D8D2C4]">This cannot be undone. Clear them?</span>
          <button
            data-testid="reset-stats-confirm-btn"
            onClick={wipe}
            disabled={busy}
            className="rounded-full bg-[#E07A5F] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#121417] disabled:opacity-50"
          >
            {busy ? "clearing" : "yes, clear everything"}
          </button>
          <button data-testid="reset-stats-cancel-btn" onClick={() => setArmed(false)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E7582]">
            keep them
          </button>
        </div>
      )}
    </div>
  );
};
